import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Chip, Divider } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import ComputerIcon from '@mui/icons-material/Computer';
import StorageIcon from '@mui/icons-material/Storage';
import BarChartIcon from '@mui/icons-material/BarChart';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AppsIcon from '@mui/icons-material/Apps';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import StarIcon from '@mui/icons-material/Star';
import { resolveIcon, getCategoryLabel } from './appStoreUtils';

function MetadataItem({ icon, label, value }) {
  if (!value && value !== 0) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
      {icon}
      <Typography variant="body2" color="text.secondary">
        <strong>{label}:</strong> {value}
      </Typography>
    </Box>
  );
}

function AppDetailModal({ app, open, onClose }) {
  if (!app) return null;

  const meta = app.metadata || {};
  const hasMetadata = app.source === 'appvol' && Object.keys(meta).length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flexShrink: 0 }}>
            {resolveIcon(app)}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {app.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
              <Chip
                label={getCategoryLabel(app.category)}
                size="small"
                color="primary"
                variant="outlined"
              />
              {app.version && (
                <Typography variant="caption" color="text.secondary">
                  v{app.version}
                </Typography>
              )}
              {app.size && (
                <Typography variant="caption" color="text.secondary">
                  {app.size}
                </Typography>
              )}
              {app.isCurrent && (
                <Chip
                  icon={<StarIcon sx={{ fontSize: '14px !important' }} />}
                  label="CURRENT"
                  size="small"
                  color="success"
                  variant="filled"
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ minHeight: 120 }}>
        {/* Descrizione testuale / nota */}
        {app.description ? (
          <Box sx={{ '& p': { mt: 0 }, '& img': { maxWidth: '100%' }, mb: 2 }}>
            <ReactMarkdown>{app.description}</ReactMarkdown>
          </Box>
        ) : meta.note ? (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {meta.note}
          </Typography>
        ) : null}

        {/* Metadati AppVol */}
        {hasMetadata && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Dettagli applicazione
            </Typography>
            <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 2, p: 2 }}>
              <MetadataItem
                icon={<InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: '#7f8c8d' }} />}
                label="Nome package"
                value={meta.filename}
              />
              <MetadataItem
                icon={<ComputerIcon sx={{ fontSize: 18, color: '#7f8c8d' }} />}
                label="Sistema operativo"
                value={meta.osName}
              />
              <MetadataItem
                icon={<StorageIcon sx={{ fontSize: 18, color: '#7f8c8d' }} />}
                label="Dimensione"
                value={app.size}
              />
              <MetadataItem
                icon={<AppsIcon sx={{ fontSize: 18, color: '#7f8c8d' }} />}
                label="Programmi inclusi"
                value={meta.programsCount > 0 ? meta.programsCount : null}
              />
              <MetadataItem
                icon={<CloudDownloadIcon sx={{ fontSize: 18, color: '#7f8c8d' }} />}
                label="Distribuzione"
                value={meta.delivery ? `${meta.delivery} (${meta.format || 'N/A'})` : null}
              />
              <MetadataItem
                icon={<BarChartIcon sx={{ fontSize: 18, color: '#7f8c8d' }} />}
                label="Utilizzi totali"
                value={meta.totalUseCount > 0 ? meta.totalUseCount : null}
              />
              <MetadataItem
                icon={<BarChartIcon sx={{ fontSize: 18, color: '#7f8c8d' }} />}
                label="Picco utilizzo"
                value={meta.peakUsage > 0 ? meta.peakUsage : null}
              />
              <MetadataItem
                icon={<CalendarTodayIcon sx={{ fontSize: 18, color: '#7f8c8d' }} />}
                label="Creato il"
                value={meta.createdAt}
              />
              <MetadataItem
                icon={<CalendarTodayIcon sx={{ fontSize: 18, color: '#7f8c8d' }} />}
                label="Aggiornato il"
                value={meta.updatedAt}
              />
            </Box>
          </Box>
        )}

        {/* Descrizione per pacchetti DevOps senza metadati */}
        {app.source === 'devops' && !app.description && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Nessuna descrizione disponibile per questo pacchetto.
          </Typography>
        )}

        {/* Sorgente */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.disabled">
            Sorgente: {app.source === 'appvol' ? 'Omnissa App Volumes' : 'Repository LIBaaS'}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Chiudi</Button>
      </DialogActions>
    </Dialog>
  );
}

export default AppDetailModal;
